import React, { useEffect, useState } from "react";
import "./AssetLoadingGate.css";

const files = require.context(
  ".",
  true,
  /\\.(png|jpe?g|webp|gif|svg|mp3|wav|ogg|mp4|webm|woff2?|ttf)$/i
);

const assets = Array.from(new Set(
  files.keys().map((key) => {
    const value = files(key);
    return typeof value === "string" ? value : value?.default;
  }).filter(Boolean)
));

export default function AssetLoadingGate({ children }) {
  const [loaded, setLoaded] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;
    let cursor = 0;
    let completed = 0;

    setLoaded(0);
    setReady(false);
    setError("");

    async function worker() {
      while (!stopped) {
        const index = cursor++;
        if (index >= assets.length) return;

        const response = await fetch(assets[index], {
          cache: "force-cache",
          credentials: "same-origin",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Accessory failed: " + response.status);
        }

        await response.blob();
        completed += 1;

        if (!stopped) setLoaded(completed);
      }
    }

    Promise.all(
      Array.from(
        { length: Math.min(6, Math.max(1, assets.length)) },
        () => worker()
      )
    )
      .then(() => document.fonts?.ready)
      .then(() => {
        if (!stopped) {
          setLoaded(assets.length);
          setTimeout(() => {
            if (!stopped) setReady(true);
          }, 250);
        }
      })
      .catch((loadError) => {
        if (!stopped && loadError?.name !== "AbortError") {
          setError(
            loadError?.message ||
            "Some accessories could not be loaded."
          );
        }
      });

    return () => {
      stopped = true;
      controller.abort();
    };
  }, [attempt]);

  if (ready) return children;

  const total = assets.length;
  const percent = total
    ? Math.round((loaded / total) * 100)
    : 100;

  return (
    <div className="asset-loader">
      <div className="asset-loader-card">
        <div className="asset-spinner" />
        <h1>Fruit Loop</h1>

        <p>
          {error ? "Loading interrupted" : "Loading game accessories"}
          {!error && <span className="asset-dots" />}
        </p>

        <div className="asset-progress">
          <i style={{ width: percent + "%" }} />
        </div>

        <div className="asset-details">
          <span>
            {loaded.toLocaleString()} of{" "}
            {total.toLocaleString()} accessories
          </span>
          <strong>{percent}%</strong>
        </div>

        {error && (
          <>
            <div className="asset-error">{error}</div>
            <button
              type="button"
              onClick={() => setAttempt((value) => value + 1)}
            >
              Retry
            </button>
          </>
        )}
      </div>
    </div>
  );
}
